import React, { useEffect, useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Autocomplete from "@mui/material/Autocomplete";
import { getFirestore, collection, query, where, orderBy, limit, getDocs } from "firebase/firestore";
import moment from "moment";
import { utils, writeFile } from "xlsx";

const firestore = getFirestore();

const EVENT_TYPES = [
    { value: "", label: "Todos" },
    { value: "entrada", label: "Entradas por remito" },
    { value: "salida", label: "Salidas por remito" },
    { value: "consumo", label: "Consumos en orden" },
    { value: "ajuste", label: "Modificaciones manuales" }
];

function normalizeId(value) {
    if (value === null || value === undefined) return "";
    return String(value).trim();
}

function toNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
}

function parseFechaToYYYYMMDD(fecha) {
    if (!fecha) return "";

    if (typeof fecha === "number") {
        const m = moment(fecha);
        return m.isValid() ? m.format("YYYYMMDD") : "";
    }

    if (typeof fecha === "object") {
        if (typeof fecha.toDate === "function") {
            const m = moment(fecha.toDate());
            return m.isValid() ? m.format("YYYYMMDD") : "";
        }
        if (typeof fecha.seconds === "number") {
            const m = moment(fecha.seconds * 1000);
            return m.isValid() ? m.format("YYYYMMDD") : "";
        }
    }

    const str = String(fecha).trim();
    if (!str) return "";

    if (/^\d{8}$/.test(str)) return str;
    if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10).replaceAll("-", "");

    // Soportar formatos con o sin ceros iniciales y con o sin hora/segundos
    const slashMatch = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
    if (slashMatch) {
        const day = slashMatch[1].padStart(2, "0");
        const month = slashMatch[2].padStart(2, "0");
        const year = slashMatch[3];
        return `${year}${month}${day}`;
    }

    const parsed = moment(
        str,
        [
            "DD/MM/YYYY HH:mm:ss",
            "DD/MM/YYYY HH:mm",
            "DD/MM/YYYY",
            "D/M/YYYY HH:mm:ss",
            "D/M/YYYY HH:mm",
            "D/M/YYYY",
            "YYYYMMDD",
            moment.ISO_8601
        ],
        false
    );
    return parsed.isValid() ? parsed.format("YYYYMMDD") : "";
}

function getEventDelta(evento) {
    const qty = toNumber(evento?.cantidad);
    if (evento?.tipo === "entrada") return qty;
    if (evento?.tipo === "salida") return -qty;
    if (evento?.tipo === "consumo") return -qty;
    if (evento?.tipo === "ajuste") return qty;
    return 0;
}

function formatFecha(fecha) {
    const parsed = parseFechaToYYYYMMDD(fecha);
    if (!parsed) return fecha || "-";
    return moment(parsed, "YYYYMMDD").format("DD/MM/YYYY");
}

function getEventTimestamp(fecha) {
    const parsed = parseFechaToYYYYMMDD(fecha);
    return parsed ? moment(parsed, "YYYYMMDD").valueOf() : 0;
}

function getMaterialId(material) {
    return normalizeId(material?.id ?? material?.materialId ?? material?.codigo ?? material?.codigos);
}

function getMaterialQuantity(material) {
    return toNumber(material?.cantidad ?? material?.value ?? material?.cantidadR ?? material?.cantidadV ?? material?.qty);
}

function getOrdenMateriales(orden) {
    return orden?.materials || orden?.materiales || orden?.materialesPrecargados || orden?.selectedOrderInfo?.materiales || [];
}

function getCuadrillaLabel(cuadrilla) {
    if (!cuadrilla) return "";
    const numero = cuadrilla.numero || cuadrilla.codCuadrilla || cuadrilla.codigo || cuadrilla.name || "";
    const usuario = cuadrilla.usuario || cuadrilla.nombre || cuadrilla.descripcion || "";
    return [numero, usuario].filter(Boolean).join(" - ") || String(cuadrilla.id || "");
}

function getResumen(evento) {
    if (evento.tipo === "consumo") {
        return `OT: ${evento.nroOt || "-"} · Cliente: ${evento.cliente || "-"} · ${evento.tipoOt || "Orden"}`;
    }

    if (evento.tipo === "ajuste") {
        return `Stock: ${evento.cantidadAnterior ?? "-"} -> ${evento.cantidadStock ?? "-"} · Diferencia: ${evento.cantidad >= 0 ? "+" : ""}${evento.cantidad}`;
    }

    return `Remito: ${evento.idEntidad || "-"} · ${evento.origen || "-"} -> ${evento.destino || "-"}`;
}

function getTipoColor(tipo) {
    if (tipo === "entrada") return "success";
    if (tipo === "salida") return "warning";
    if (tipo === "consumo") return "error";
    if (tipo === "ajuste") return "info";
    return "default";
}

function MovimientosMateriales() {
    const [materialsList, setMaterialsList] = useState([]);
    const [cuadrillasList, setCuadrillasList] = useState([]);
    const [selectedMaterial, setSelectedMaterial] = useState(null);
    const [selectedCuadrilla, setSelectedCuadrilla] = useState(null);
    const [loadingInitial, setLoadingInitial] = useState(true);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [allEvents, setAllEvents] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [stockActual, setStockActual] = useState(null);
    const [showInfoHelp, setShowInfoHelp] = useState(true);
    const [filters, setFilters] = useState({
        fechaDesde: "",
        fechaHasta: "",
        tipo: ""
    });

    useEffect(() => {
        const fetchCatalogs = async () => {
            setLoadingInitial(true);
            try {
                const [materialesSnapshot, cuadrillasSnapshot] = await Promise.all([
                    getDocs(query(collection(firestore, "materiales"), orderBy("id", "asc"))),
                    getDocs(query(collection(firestore, "cuadrillas"), orderBy("id", "asc")))
                ]);

                setMaterialsList(materialesSnapshot.docs.map((docSnap) => ({ docId: docSnap.id, ...docSnap.data() })));
                setCuadrillasList(cuadrillasSnapshot.docs.map((docSnap) => ({ docId: docSnap.id, ...docSnap.data() })));
            } catch (error) {
                console.error("Error cargando catálogos:", error);
                setMaterialsList([]);
                setCuadrillasList([]);
            } finally {
                setLoadingInitial(false);
            }
        };

        fetchCatalogs();
    }, []);

    // Recalcular automáticamente los eventos del período, Stock Inicial y Stock Final
    // cada vez que cambian los datos buscados o el rango de fechas seleccionado
    const {
        events,
        stockInicialPeriod,
        stockFinalPeriod,
        totals
    } = useMemo(() => {
        const stockHoy = stockActual ? toNumber(stockActual.cantidad) : 0;
        if (!hasSearched) {
            return {
                events: [],
                stockInicialPeriod: 0,
                stockFinalPeriod: 0,
                totals: { entradas: 0, salidas: 0, consumos: 0, ajustes: 0 }
            };
        }

        const desde = filters.fechaDesde ? filters.fechaDesde.replaceAll("-", "") : "";
        const hasta = filters.fechaHasta ? filters.fechaHasta.replaceAll("-", "") : "";

        // Ordenar todos los eventos de más reciente a más antiguo
        const sortedDesc = [...allEvents].sort((a, b) => {
            const tsA = getEventTimestamp(a.fecha);
            const tsB = getEventTimestamp(b.fecha);
            if (tsA !== tsB) return tsB - tsA;
            return String(b.key).localeCompare(String(a.key));
        });

        // Recorrer hacia atrás desde el Stock Actual (hoy) para obtener el stock en cada punto del tiempo
        let runningStock = stockHoy;
        const eventsWithRunningStock = [];

        for (const event of sortedDesc) {
            const stockResultante = runningStock;
            const delta = getEventDelta(event);
            const stockInicial = runningStock - delta;

            eventsWithRunningStock.push({
                ...event,
                fechaNorm: parseFechaToYYYYMMDD(event.fecha),
                stockInicial,
                stockResultante
            });

            runningStock = stockInicial;
        }

        // Filtrar eventos dentro del rango [desde, hasta]
        // Si hay filtro de fechas activo, descartar eventos sin fecha válida para no distorsionar el stock inicial del período
        const periodEvents = eventsWithRunningStock.filter((evento) => {
            const fecha = evento.fechaNorm;
            if ((desde || hasta) && !fecha) return false;
            if (desde && fecha < desde) return false;
            if (hasta && fecha > hasta) return false;
            return true;
        });

        // Calcular el Stock Final al cierre del período (fechaHasta)
        // Es el Stock Actual (hoy) menos el efecto neto de todos los movimientos ocurridos DESPUÉS de fechaHasta
        let stockFinalVal = stockHoy;
        if (hasta) {
            for (const ev of eventsWithRunningStock) {
                if (ev.fechaNorm && ev.fechaNorm > hasta) {
                    stockFinalVal -= getEventDelta(ev);
                }
            }
        }

        // Calcular totales del período seleccionado
        const periodTotals = periodEvents.reduce(
            (acc, evento) => {
                if (evento.tipo === "entrada") acc.entradas += toNumber(evento.cantidad);
                if (evento.tipo === "salida") acc.salidas += toNumber(evento.cantidad);
                if (evento.tipo === "consumo") acc.consumos += toNumber(evento.cantidad);
                if (evento.tipo === "ajuste") acc.ajustes += toNumber(evento.cantidad);
                return acc;
            },
            { entradas: 0, salidas: 0, consumos: 0, ajustes: 0 }
        );

        // El Stock Inicial al comienzo del período (fechaDesde) es exactamente:
        // Stock Final del Período - (Entradas - Salidas - Consumos + Ajustes del período)
        const netPeriodChange =
            periodTotals.entradas -
            periodTotals.salidas -
            periodTotals.consumos +
            periodTotals.ajustes;
        const stockInicialVal = stockFinalVal - netPeriodChange;

        return {
            events: periodEvents,
            stockInicialPeriod: stockInicialVal,
            stockFinalPeriod: stockFinalVal,
            totals: periodTotals
        };
    }, [allEvents, stockActual, hasSearched, filters.fechaDesde, filters.fechaHasta]);

    const filteredEvents = useMemo(() => {
        return events
            .filter((evento) => !filters.tipo || evento.tipo === filters.tipo)
            .sort((a, b) => getEventTimestamp(b.fecha) - getEventTimestamp(a.fecha));
    }, [events, filters.tipo]);

    const fetchStockActual = async (idCuadrilla, idMaterial) => {
        const docId = `${idCuadrilla}c${idMaterial}`;
        const stockByDocQuery = query(collection(firestore, "materialsxcuadrilla"), where("idCuadrilla", "==", Number(idCuadrilla)), where("idMaterial", "==", Number(idMaterial)), limit(1));
        const snapshot = await getDocs(stockByDocQuery);
        const stockDoc = snapshot.docs.find((docSnap) => docSnap.id === docId) || snapshot.docs[0];
        return stockDoc ? { docId: stockDoc.id, ...stockDoc.data() } : null;
    };

    const fetchRemitos = async (idCuadrilla) => {
        const remitosCollection = collection(firestore, "remitos");
        const q1 = query(remitosCollection, where("idCuadrillaEntrega", "==", Number(idCuadrilla)));
        const q2 = query(remitosCollection, where("idCuadrillaReceptora", "==", Number(idCuadrilla)));
        
        const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
        const map = new Map();
        snap1.docs.forEach(docSnap => map.set(docSnap.id, { docId: docSnap.id, ...docSnap.data() }));
        snap2.docs.forEach(docSnap => map.set(docSnap.id, { docId: docSnap.id, ...docSnap.data() }));
        return Array.from(map.values());
    };

    const fetchOrdenes = async (idCuadrilla) => {
        const ordenesCollection = collection(firestore, "ordenes");
        const q1 = query(ordenesCollection, where("idCuadrilla", "==", Number(idCuadrilla)));
        const q2 = query(ordenesCollection, where("idCuadrilla", "==", String(idCuadrilla)));
        
        const [snap1, snap2] = await Promise.all([getDocs(q1), getDocs(q2)]);
        const map = new Map();
        snap1.docs.forEach(docSnap => map.set(docSnap.id, { docId: docSnap.id, ...docSnap.data() }));
        snap2.docs.forEach(docSnap => map.set(docSnap.id, { docId: docSnap.id, ...docSnap.data() }));
        return Array.from(map.values());
    };

    const buildRemitoEvents = (remitos, idCuadrilla, idMaterial) => {
        return remitos.flatMap((remito) => {
            const material = (remito.materials || remito.materiales || []).find((item) => getMaterialId(item) === idMaterial);
            if (!material) return [];

            const eventsForRemito = [];
            const cantidad = getMaterialQuantity(material);
            const idEntrega = normalizeId(remito.idCuadrillaEntrega);
            const idReceptora = normalizeId(remito.idCuadrillaReceptora);
            const base = {
                key: `remito-${remito.docId || remito.id}`,
                idEntidad: remito.id || remito.docId,
                fecha: remito.fecha || remito.confectionDate,
                materialNombre: material.name || selectedMaterial?.name || selectedMaterial?.nombre || "-",
                cantidad,
                origen: remito.cuadrillaEntrega || idEntrega || "-",
                destino: remito.cuadrillaReceptora || idReceptora || "-",
                observaciones: remito.observaciones || "-",
                usuario: remito.emailCuadrillaReceptora || "-"
            };

            if (idReceptora === idCuadrilla) {
                eventsForRemito.push({ ...base, key: `${base.key}-entrada`, tipo: "entrada" });
            }

            if (idEntrega === idCuadrilla) {
                eventsForRemito.push({ ...base, key: `${base.key}-salida`, tipo: "salida" });
            }

            return eventsForRemito;
        });
    };

    const buildOrdenEvents = (ordenes, idCuadrilla, idMaterial) => {
        return ordenes.flatMap((orden) => {
            if (normalizeId(orden.idCuadrilla) !== idCuadrilla) return [];

            const material = getOrdenMateriales(orden).find((item) => getMaterialId(item) === idMaterial);
            if (!material) return [];

            return [{
                key: `orden-${orden.docId || orden.id || orden.nroOt}`,
                tipo: "consumo",
                idEntidad: orden.nroOt || orden.id || orden.docId,
                fecha: orden.fecha || orden.date || orden.confectionDate || orden.confectionData || orden.selectedOrderInfo?.fecha,
                materialNombre: material.name || selectedMaterial?.name || selectedMaterial?.nombre || "-",
                cantidad: getMaterialQuantity(material),
                origen: orden.cuadrilla || orden.codCuadrilla || idCuadrilla,
                destino: "Orden de trabajo",
                nroOt: orden.nroOt || orden.selectedOrderInfo?.nroOt,
                tipoOt: orden.tipoOt,
                cliente: orden.cliente || orden.selectedOrderInfo?.cliente,
                observaciones: orden.comments || orden.comentarios || orden.selectedOrderInfo?.comentarios || "-",
                usuario: orden.user || orden.usuarioCuadrilla || "-"
            }];
        });
    };

    const buildStockEvents = (stock, idCuadrilla, idMaterial) => {
        if (!stock || !Array.isArray(stock.historialStock)) return [];

        return stock.historialStock.map((registro, index) => {
            const cantidad = toNumber(registro.diferencia ?? toNumber(registro.cantidad) - toNumber(registro.cantidadAnterior));

            return {
                key: `stock-${stock.docId || `${idCuadrilla}c${idMaterial}`}-${registro.fecha || index}`,
                tipo: "ajuste",
                idEntidad: stock.docId || `${idCuadrilla}c${idMaterial}`,
                fecha: registro.fecha,
                materialNombre: selectedMaterial?.name || selectedMaterial?.nombre || "-",
                cantidad,
                cantidadAnterior: registro.cantidadAnterior,
                cantidadStock: registro.cantidad,
                origen: selectedCuadrilla ? getCuadrillaLabel(selectedCuadrilla) : idCuadrilla,
                destino: "Stock manual",
                observaciones: registro.tipo || "edicion",
                usuario: registro.usuario || "-"
            };
        });
    };

    const handleSearch = async () => {
        if (!selectedMaterial || !selectedCuadrilla) {
            setAllEvents([]);
            setHasSearched(false);
            setStockActual(null);
            return;
        }

        setLoadingSearch(true);
        try {
            const idMaterial = normalizeId(selectedMaterial.id ?? selectedMaterial.materialId);
            const idCuadrilla = normalizeId(selectedCuadrilla.id);

            const [stock, remitos, ordenes] = await Promise.all([
                fetchStockActual(idCuadrilla, idMaterial),
                fetchRemitos(idCuadrilla),
                fetchOrdenes(idCuadrilla)
            ]);

            const allEventsRaw = [
                ...buildRemitoEvents(remitos, idCuadrilla, idMaterial),
                ...buildOrdenEvents(ordenes, idCuadrilla, idMaterial),
                ...buildStockEvents(stock, idCuadrilla, idMaterial)
            ];

            setStockActual(stock);
            setAllEvents(allEventsRaw);
            setHasSearched(true);
        } catch (error) {
            console.error("Error buscando movimientos de materiales:", error);
            setStockActual(null);
            setAllEvents([]);
            setHasSearched(false);
        } finally {
            setLoadingSearch(false);
        }
    };

    const exportToExcel = () => {
        if (filteredEvents.length === 0) return;

        const wb = utils.book_new();

        // ── Hoja 1: Resumen ──────────────────────────────────────────────────
        const cuadrillaLabel = selectedCuadrilla ? getCuadrillaLabel(selectedCuadrilla) : '-';
        const materialLabel = selectedMaterial ? (selectedMaterial.name || selectedMaterial.nombre || String(selectedMaterial.id)) : '-';
        const periodoDesde = filters.fechaDesde ? moment(filters.fechaDesde).format('DD/MM/YYYY') : 'sin límite';
        const periodoHasta = filters.fechaHasta ? moment(filters.fechaHasta).format('DD/MM/YYYY') : 'sin límite';

        const resumenData = [
            ['Movimiento de Materiales', ''],
            ['Cuadrilla', cuadrillaLabel],
            ['Material', materialLabel],
            ['Período desde', periodoDesde],
            ['Período hasta', periodoHasta],
            ['', ''],
            ['RESUMEN DEL PERÍODO', ''],
            ['Stock Inicial Estimado', hasSearched ? stockInicialPeriod : '-'],
            ['Entradas (+)', totals.entradas],
            ['Salidas (-)', totals.salidas],
            ['Consumos en OT (-)', totals.consumos],
            ['Ajustes (+/-)', totals.ajustes],
            ['Stock Final Estimado', hasSearched ? stockFinalPeriod : '-'],
            ['', ''],
            ['Stock Actual (hoy en Firestore)', hasSearched ? (stockActual?.cantidad ?? 0) : '-'],
        ];

        const wsResumen = utils.aoa_to_sheet(resumenData);
        wsResumen['!cols'] = [{ wch: 30 }, { wch: 30 }];
        utils.book_append_sheet(wb, wsResumen, 'Resumen');

        // ── Hoja 2: Movimientos (mismas columnas que la pantalla) ─────────────
        const detailData = filteredEvents.map(e => ({
            'Fecha': formatFecha(e.fecha),
            'Tipo': EVENT_TYPES.find(type => type.value === e.tipo)?.label || e.tipo,
            'ID': e.idEntidad || '-',
            'Material': e.materialNombre || '',
            'Cantidad': e.cantidad,
            'Detalle': getResumen(e),
            'Usuario': e.usuario || '-',
        }));

        const wsDetalle = utils.json_to_sheet(detailData);
        wsDetalle['!cols'] = [
            { wch: 14 }, // Fecha
            { wch: 22 }, // Tipo
            { wch: 16 }, // ID
            { wch: 28 }, // Material
            { wch: 12 }, // Cantidad
            { wch: 55 }, // Detalle
            { wch: 30 }, // Usuario
        ];
        utils.book_append_sheet(wb, wsDetalle, 'Movimientos');

        writeFile(wb, `movimientos_${selectedCuadrilla?.numero || 'cuadrilla'}_mat${selectedMaterial?.id || ''}_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
    };

    const clearFilters = () => {
        setSelectedMaterial(null);
        setSelectedCuadrilla(null);
        setFilters({ fechaDesde: "", fechaHasta: "", tipo: "" });
        setStockActual(null);
        setAllEvents([]);
        setHasSearched(false);
    };

    const labelDesde = filters.fechaDesde ? moment(filters.fechaDesde, "YYYY-MM-DD").format("DD/MM/YYYY") : null;
    const labelHasta = filters.fechaHasta ? moment(filters.fechaHasta, "YYYY-MM-DD").format("DD/MM/YYYY") : null;
    const hasDateFilter = Boolean(labelDesde || labelHasta);

    return (
        <Container component="main" sx={{ marginTop: 2, marginBottom: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, marginBottom: 2 }}>
                <Typography component="h1" variant="h5">
                    Registro de movimientos de materiales
                </Typography>
                <Button
                    size="small"
                    variant="text"
                    startIcon={<HelpOutlineIcon />}
                    onClick={() => setShowInfoHelp((prev) => !prev)}
                >
                    {showInfoHelp ? "Ocultar guía de valores" : "Ver qué significa cada valor"}
                </Button>
            </Box>

            <Collapse in={showInfoHelp}>
                <Alert
                    severity="info"
                    icon={<InfoOutlinedIcon fontSize="inherit" />}
                    onClose={() => setShowInfoHelp(false)}
                    sx={{ marginBottom: 2, "& .MuiAlert-message": { width: "100%" } }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                        ¿Cómo se calculan y qué significan los valores de esta pantalla?
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        El sistema toma como punto de partida real el <strong>Stock Actual (hoy)</strong> de la cuadrilla y reconstruye el historial hacia atrás según los movimientos registrados. Cuando seleccionás un <strong>rango de fechas</strong>, los indicadores muestran únicamente el balance de ese período siguiendo la fórmula:
                        {" "}
                        <strong>Stock Inicial Estimado + Entradas − Salidas − Consumos en OT ± Ajustes = Stock Final Estimado</strong>.
                    </Typography>
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                            gap: 1,
                            fontSize: "0.8rem",
                            color: "text.secondary",
                            backgroundColor: "rgba(255,255,255,0.65)",
                            p: 1.25,
                            borderRadius: 1
                        }}
                    >
                        <Box>• <strong>Stock Inicial Estimado:</strong> Cantidad estimada que tenía la cuadrilla justo al comenzar la <em>Fecha desde</em> seleccionada (antes de los movimientos del período).</Box>
                        <Box>• <strong>Entradas (+):</strong> Materiales recibidos por la cuadrilla mediante remitos en el período.</Box>
                        <Box>• <strong>Salidas (-):</strong> Materiales entregados o devueltos por la cuadrilla mediante remitos en el período.</Box>
                        <Box>• <strong>Consumos en OT (-):</strong> Materiales utilizados y descargados en Órdenes de Trabajo durante el período.</Box>
                        <Box>• <strong>Ajustes (+/-):</strong> Diferencia neta por ediciones manuales de stock realizadas en la pantalla de Stock durante el período.</Box>
                        <Box>• <strong>Stock Final Estimado:</strong> Cantidad estimada al cierre de la <em>Fecha hasta</em> seleccionada (después de aplicar todos los movimientos del período).</Box>
                        <Box>• <strong>Stock Actual (hoy):</strong> Cantidad real que tiene la cuadrilla hoy en el sistema (sin importar el filtro de fechas).</Box>
                    </Box>
                </Alert>
            </Collapse>

            <Paper sx={{ padding: 2, marginBottom: 2 }}>
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}>
                    <Autocomplete
                        options={cuadrillasList}
                        loading={loadingInitial}
                        size="small"
                        value={selectedCuadrilla}
                        isOptionEqualToValue={(option, value) => normalizeId(option?.id) === normalizeId(value?.id)}
                        getOptionLabel={getCuadrillaLabel}
                        onChange={(_, value) => setSelectedCuadrilla(value)}
                        renderInput={(params) => <TextField {...params} label="Cuadrilla" placeholder="Seleccione una cuadrilla" />}
                    />
                    <Autocomplete
                        options={materialsList}
                        loading={loadingInitial}
                        size="small"
                        value={selectedMaterial}
                        isOptionEqualToValue={(option, value) => normalizeId(option?.id) === normalizeId(value?.id)}
                        getOptionLabel={(option) => option?.name || option?.nombre || String(option?.id || "")}
                        onChange={(_, value) => setSelectedMaterial(value)}
                        renderInput={(params) => <TextField {...params} label="Material" placeholder="Seleccione un material" />}
                    />
                    <TextField
                        label="Fecha desde"
                        type="date"
                        size="small"
                        value={filters.fechaDesde}
                        onChange={(e) => setFilters((prev) => ({ ...prev, fechaDesde: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Fecha hasta"
                        type="date"
                        size="small"
                        value={filters.fechaHasta}
                        onChange={(e) => setFilters((prev) => ({ ...prev, fechaHasta: e.target.value }))}
                        InputLabelProps={{ shrink: true }}
                    />
                    <FormControl size="small">
                        <InputLabel id="filtro-tipo-movimiento">Tipo</InputLabel>
                        <Select
                            labelId="filtro-tipo-movimiento"
                            value={filters.tipo}
                            label="Tipo"
                            onChange={(e) => setFilters((prev) => ({ ...prev, tipo: e.target.value }))}
                        >
                            {EVENT_TYPES.map((type) => (
                                <MenuItem key={type.value || "all"} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1, marginTop: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        {hasDateFilter
                            ? `Período activo: ${labelDesde ? `desde el ${labelDesde}` : "desde el inicio"} ${labelHasta ? `hasta el ${labelHasta}` : "hasta hoy"}`
                            : "Período activo: Historial completo (sin filtro de fechas)"}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1 }}>
                        <Button variant="outlined" onClick={clearFilters}>
                            Limpiar
                        </Button>
                        {filteredEvents.length > 0 && (
                            <Button variant="outlined" color="success" onClick={exportToExcel}>
                                Exportar a Excel
                            </Button>
                        )}
                        <Button variant="contained" onClick={handleSearch} disabled={loadingSearch || !selectedCuadrilla || !selectedMaterial}>
                            Buscar
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", marginBottom: 2 }}>
                <Tooltip
                    title="Cantidad estimada disponible al inicio del período seleccionado, antes de sumar o restar los movimientos de ese rango de fechas."
                    arrow
                >
                    <Paper sx={{ padding: 2, borderLeft: '4px solid #1976d2' }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>Stock Inicial Estimado</Typography>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                        </Box>
                        <Typography variant="h5">{hasSearched ? stockInicialPeriod : "-"}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.2 }}>
                            {labelDesde ? `Al iniciar el ${labelDesde}` : "Al inicio del historial"}
                        </Typography>
                    </Paper>
                </Tooltip>

                <Tooltip
                    title="Total de unidades ingresadas a la cuadrilla mediante remitos de entrega o transferencia durante el período seleccionado."
                    arrow
                >
                    <Paper sx={{ padding: 2, borderLeft: '4px solid #2e7d32' }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>Entradas (+)</Typography>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                        </Box>
                        <Typography variant="h5" color="success.main">{totals.entradas}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.2 }}>
                            Recibido por remitos en el período
                        </Typography>
                    </Paper>
                </Tooltip>

                <Tooltip
                    title="Total de unidades retiradas o devueltas desde la cuadrilla mediante remitos durante el período seleccionado."
                    arrow
                >
                    <Paper sx={{ padding: 2, borderLeft: '4px solid #ed6c02' }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>Salidas (-)</Typography>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                        </Box>
                        <Typography variant="h5" color="warning.main">{totals.salidas}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.2 }}>
                            Entregado/devuelto por remitos
                        </Typography>
                    </Paper>
                </Tooltip>

                <Tooltip
                    title="Total de unidades consumidas y descargadas en Órdenes de Trabajo (OT) dentro del período seleccionado."
                    arrow
                >
                    <Paper sx={{ padding: 2, borderLeft: '4px solid #d32f2f' }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>Consumos en OT (-)</Typography>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                        </Box>
                        <Typography variant="h5" color="error.main">{totals.consumos}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.2 }}>
                            Usado en órdenes del período
                        </Typography>
                    </Paper>
                </Tooltip>

                <Tooltip
                    title="Suma neta de diferencias por modificaciones manuales de stock realizadas directamente en la pantalla de Stock durante el período."
                    arrow
                >
                    <Paper sx={{ padding: 2, borderLeft: '4px solid #0288d1' }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>Ajustes (+/-)</Typography>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                        </Box>
                        <Typography variant="h5" color="info.main">{totals.ajustes >= 0 ? `+${totals.ajustes}` : totals.ajustes}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.2 }}>
                            Ediciones manuales en el período
                        </Typography>
                    </Paper>
                </Tooltip>

                <Tooltip
                    title="Stock estimado resultante al finalizar el período seleccionado (Stock Inicial + Entradas - Salidas - Consumos ± Ajustes)."
                    arrow
                >
                    <Paper sx={{ padding: 2, borderLeft: '4px solid #9c27b0' }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>Stock Final Estimado</Typography>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                        </Box>
                        <Typography variant="h5">{hasSearched ? stockFinalPeriod : "-"}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.2 }}>
                            {labelHasta ? `Al cierre del ${labelHasta}` : "Al cierre del período (hoy)"}
                        </Typography>
                    </Paper>
                </Tooltip>

                <Tooltip
                    title="Cantidad real registrada hoy en el stock de la cuadrilla, independientemente de las fechas filtradas."
                    arrow
                >
                    <Paper sx={{ padding: 2, bgcolor: '#f5f5f5', borderLeft: '4px solid #616161' }}>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="body2" color="text.secondary" fontWeight={600}>Stock Actual (hoy)</Typography>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.disabled" }} />
                        </Box>
                        <Typography variant="h5" fontWeight="bold">{hasSearched ? (stockActual?.cantidad ?? 0) : "-"}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5, lineHeight: 1.2 }}>
                            Valor real en stock al día de hoy
                        </Typography>
                    </Paper>
                </Tooltip>
            </Box>

            {loadingSearch ? (
                <Box display="flex" justifyContent="center" py={4}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table size="small" aria-label="Registro de movimientos de materiales">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Fecha</strong></TableCell>
                                <TableCell><strong>Tipo</strong></TableCell>
                                <TableCell><strong>ID</strong></TableCell>
                                <TableCell><strong>Material</strong></TableCell>
                                <TableCell align="right"><strong>Cantidad</strong></TableCell>
                                <TableCell><strong>Detalle</strong></TableCell>
                                <TableCell><strong>Usuario</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredEvents.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        No hay movimientos para los filtros seleccionados
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredEvents.map((evento) => (
                                    <TableRow key={evento.key}>
                                        <TableCell>{formatFecha(evento.fecha)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                size="small"
                                                label={EVENT_TYPES.find((type) => type.value === evento.tipo)?.label || evento.tipo}
                                                color={getTipoColor(evento.tipo)}
                                                variant={evento.tipo === "salida" || evento.tipo === "ajuste" ? "outlined" : "filled"}
                                            />
                                        </TableCell>
                                        <TableCell>{evento.idEntidad || "-"}</TableCell>
                                        <TableCell>{evento.materialNombre}</TableCell>
                                        <TableCell align="right">{evento.cantidad}</TableCell>
                                        <TableCell sx={{ maxWidth: 440 }}>
                                            {getResumen(evento)}
                                        </TableCell>
                                        <TableCell>{evento.usuario || "-"}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Container>
    );
}

export default MovimientosMateriales;
